import { getBaseRole } from '@/helpers/customRoles';
import { FC, useEffect, useMemo, useState } from 'react'
import { Tooltip } from 'antd'
import { InfoCircleOutlined } from '@ant-design/icons'
import { useAsync, useAsyncRetry } from 'react-use'
import cn from 'classnames'
import Modal from '@common/Modal'
import Button from '@UI/New/Button'
import Select from '../base/Select/Select'
import { IcoRemove } from '@/utils/icons'
import { crmApi } from '@/api'
import { isError } from '@/helpers/checkQuery'
import { Lead } from '@/api/types/crm/crm.types'
import { useAddAlert } from '@/hooks/useAddAlert'
import { useCRMContext } from '../../CRMContext'
import { getLeadFieldValue } from '../../utils/leadFields'
import type { LeadsTableRow } from '../../utils/mapLeadsToTableRows'
import { selectProfile } from '@store/profile.reducer'
import { useSelector } from 'react-redux'
import mclasses from '../../styles/modal.module.css'
import classes from './index.module.css'

type AddFunnelLeadRef = Lead | LeadsTableRow

interface AddFunnelToLeadsModalProps {
  lead?: AddFunnelLeadRef | AddFunnelLeadRef[]
  open: boolean
  onClose: () => void
  onSuccess?: (
    payload?: { funnelId: number; funnelName: string; managerId: number | null },
  ) => Promise<void> | void
}

const AddFunnelToLeadsModal: FC<AddFunnelToLeadsModalProps> = ({
  lead,
  open,
  onClose,
  onSuccess,
}) => {
  const addAlert = useAddAlert()
  const profile = useSelector(selectProfile)
  const currentRole = profile?.role
  const selectedLeads = useMemo<AddFunnelLeadRef[]>(
    () => (Array.isArray(lead) ? lead : lead ? [lead] : []),
    [lead],
  )
  const {
    preloadingData: { funnels },
  } = useCRMContext()
  const funnelsState = useAsyncRetry(
    async () => {
      if (!open) return null
      if (selectedLeads.length === 1) {
        return await crmApi.funnels.getAvailableForLead({
          lead_id: selectedLeads[0].id,
        })
      }
      return await crmApi.funnels.getAllFunnels()
    },
    [open, selectedLeads],
  )
  const managersState = useAsync(crmApi.getAllManagers, [])
  const managers = managersState.value?.data?.data || []
  const actualFunnels = funnelsState.value
    ? funnelsState.value?.data?.data?.funnels ?? []
    : funnels

  const [funnelId, setFunnelId] = useState<number | null>(null)
  const [managerId, setManagerId] = useState<number | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const existingFunnelIds = useMemo<number[]>(() => {
    if (!selectedLeads.length) return []

    const leadFunnelSets = selectedLeads.map((item) => {
      const rawFunnels = Array.isArray((item as any).funnels)
        ? ((item as any).funnels as any[])
        : []

      return new Set(
        rawFunnels
          .map((funnel) => Number(funnel?.id))
          .filter((id) => Number.isFinite(id)),
      )
    })

    const [firstSet, ...otherSets] = leadFunnelSets
    const intersection = Array.from(firstSet).filter((funnelId) =>
      otherSets.every((set) => set.has(funnelId)),
    )

    return intersection
  }, [selectedLeads])

  useEffect(() => {
    if (!open) return
    setFunnelId(null)
    setManagerId(null)
  }, [existingFunnelIds, open])

  useEffect(() => {
    if (!managersState.error) return
    addAlert({
      title: 'Ошибка',
      text: 'Не удалось загрузить список менеджеров',
      type: 'error',
    })
  }, [managersState.error])

  useEffect(() => {
    if (!open || !funnelsState.error) return
    addAlert({
      title: 'Ошибка',
      text: 'Не удалось загрузить список воронок',
      type: 'error',
    })
  }, [addAlert, funnelsState.error, open])

  useEffect(() => {
    if (
      open &&
      (lead === undefined || (Array.isArray(lead) && lead.length === 0))
    ) {
      onClose()
    }
  }, [open, lead, onClose])

  const leadNames = useMemo<string[]>(() => {
    if (!lead) return ['-']
    if (Array.isArray(lead)) {
      return lead.reduce((acc, item) => {
        const name = getLeadFieldValue(item, 'NAME') || '(без фио)'
        if (acc.includes(name)) return acc
        return [...acc, name]
      }, [] as string[])
    }
    return [getLeadFieldValue(lead, 'NAME') || '(без фио)']
  }, [lead])

  const funnelOptions = useMemo(
    () =>
      actualFunnels.map((funnel) => ({
        value: funnel.id,
        label: existingFunnelIds.includes(funnel.id)
          ? Array.isArray(lead)
            ? `${funnel.name} (уже добавлена всем выбранным лидам)`
            : `${funnel.name} (уже добавлена)`
          : funnel.name,
        disabled: existingFunnelIds.includes(funnel.id),
      })),
    [actualFunnels, existingFunnelIds, lead],
  )

  const managerOptions = useMemo(() => {
    const withRole = managers.filter((manager: any) => !!manager?.user?.role)
    const salesManagers =
      withRole.length > 0
        ? withRole.filter((manager: any) => getBaseRole(manager?.user?.role) === 'sales-manager')
        : managers

    return salesManagers.map((manager: any) => ({
      value: Number(manager.id),
      label: manager.fio,
    }))
  }, [managers])

  const currentManagerOption = useMemo(() => {
    const profileUserId = Number(profile?.id)
    if (!Number.isFinite(profileUserId)) return null

    return (
      managerOptions.find((option) => {
        const manager = managers.find((item: any) => Number(item.id) === option.value)
        const managerUserId = Number(manager?.user?.id)
        return Number.isFinite(managerUserId) && managerUserId === profileUserId
      }) ?? null
    )
  }, [managerOptions, managers, profile?.id])

  const isSalesManager = getBaseRole(currentRole) === 'sales-manager'

  const selectedNewFunnelId = useMemo(() => {
    if (!funnelId) return null
    return existingFunnelIds.includes(funnelId) ? null : funnelId
  }, [existingFunnelIds, funnelId])

  useEffect(() => {
    if (!open || !isSalesManager) return
    setManagerId(currentManagerOption?.value ?? null)
  }, [open, isSalesManager, currentManagerOption?.value])

  const handleConfirm = async () => {
    if (!selectedNewFunnelId) {
      addAlert({
        title: 'Ошибка',
        text: 'Выберите новую воронку',
        type: 'error',
      })
      return
    }

    try {
      setSubmitting(true)
      const leadIds = Array.isArray(lead)
        ? lead.map((item) => item.id)
        : lead
        ? [lead.id]
        : []
      if (leadIds.length < 1) throw new Error('No leads selected')

      const response = await crmApi.leads.bulkAddFunnelToLeads({
        lead_ids: leadIds,
        funnel_id: selectedNewFunnelId,
        manager_id: managerId ?? null,
      })
      if (isError(response)) throw new Error()

      addAlert({
        title: 'Успешно',
        text:
          leadIds.length === 1
            ? 'Воронки добавлены выбранному лиду'
            : 'Воронки добавлены выбранным лидам',
        type: 'success',
      })
      const addedFunnel = actualFunnels.find(
        (item) => item.id === selectedNewFunnelId,
      )
      if (onSuccess) {
        await onSuccess(
          addedFunnel
            ? {
                funnelId: addedFunnel.id,
                funnelName: addedFunnel.name,
                managerId: managerId ?? null,
              }
            : undefined,
        )
      }
    } catch (error: any) {
      const backendError =
        error?.response?.data?.error ?? error?.response?.data?.message
      const errorText = Array.isArray(backendError)
        ? backendError.join('\n')
        : typeof backendError === 'string'
        ? backendError
        : 'Не удалось добавить воронку лиду'
      addAlert({
        title: 'Ошибка',
        text: errorText,
        type: 'error',
      })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal
      active={open}
      className={cn(mclasses.modalWrapper, classes.modalWrapper) as any}
      setActive={() => {
        if (submitting) return
        onClose()
      }}
    >
      <div className={mclasses.modalContainer}>
        <div className={mclasses.modalHeader}>
          <div className={mclasses.modalTitleContainer}>
            <span className={mclasses.modalHeaderTitle}>
              Выберите воронку для добавления {Array.isArray(lead) ? 'лидам' : 'лиду'}
            </span>
          </div>
          <div
            className={mclasses.closeIcon}
            onClick={() => {
              if (submitting) return
              onClose()
            }}
          >
            <IcoRemove />
          </div>
        </div>

        <div className={cn(mclasses.formWrapper, classes.formWrapper)}>
          <div>
            <p className={mclasses.label}>
              {Array.isArray(lead) ? 'Выбранные лиды' : 'Выбранный лид'}
            </p>
            <div className={classes.leadsList}>
              {leadNames.map((name, index) => (
                <p key={`${name}-${index}`} className={classes.leadItem}>
                  {name}
                </p>
              ))}
            </div>
          </div>

          <div>
            <p className={mclasses.label}>Выберите воронку</p>
            <Select
              showSearch
              placeholder="Выберите воронку"
              value={funnelId ?? undefined}
              onChange={(value) => setFunnelId(Number(value))}
              options={funnelOptions}
              disabled={submitting}
            />
          </div>

          <div>
            <div className={classes.managerLabel}>
              <p className={mclasses.label}>Выберите менеджера по продажам</p>
              <Tooltip
                placement="top"
                title="Выберите из списка менеджеров по продажам того, кто будет работать с этими лидами в рамках выбранной воронки"
              >
                <InfoCircleOutlined
                  className={classes.infoIcon}
                  onPointerEnterCapture={undefined}
                  onPointerLeaveCapture={undefined}
                />
              </Tooltip>
            </div>

            <Select
              showSearch
              allowClear={!isSalesManager}
              placeholder="Выберите менеджера"
              value={managerId ?? undefined}
              onChange={(value) =>
                setManagerId(
                  value === undefined || value === null || value === ''
                    ? null
                    : Number(value),
                )
              }
              options={managerOptions}
              loading={managersState.loading}
              disabled={submitting || isSalesManager}
            />
          </div>
        </div>

        <div className={mclasses.actions}>
          <Button color="gray" onClick={onClose} disabled={submitting}>
            Отменить
          </Button>
          <Button
            color="primary"
            onClick={handleConfirm}
            disabled={!selectedNewFunnelId || submitting}
            isLoading={submitting}
          >
            Добавить
          </Button>
        </div>
      </div>
    </Modal>
  )
}

export default AddFunnelToLeadsModal
